import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Trans } from 'react-i18next'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import {
  CircularProgress,
  TablePagination,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import JNJFooter from '../../features/jnj-footer/JNJFooter'
import styles from './Security.module.scss'
import MarginWrapper from '../../features/wrappers/MarginWrapper'

import {
  selectLoginHistoryObject,
  getLoginHistoryError,
  getLoginHistoryStatus,
  getLoginHistoryFilter,
  getLoginHistoryPagination,
  setStartDate, setEndDate, setPage, setRowsPerPage
} from '../../features/user/LoginHistorySlice'
import {
  formatLastLogin,
  formatApplication,
  formatDeviceSystem,
  formatLocation,
} from './SecurityFormatters'

const renderLoginHistoryMobile = (item, index, t) => (
  <div
    key={item.id}
    className={index % 2 === 0 ? styles.cardMobile : styles.cardMobileGrey}
  >

    <div className={styles.fieldBlockMobile}>
      <div className={styles.fieldLabelMobile}>{t('security-login-history-lastLogin')}</div>
      <div className={styles.fieldValueMobile}>{formatLastLogin(item?.published)}</div>
    </div>

    <div className={styles.fieldBlockMobile}>
      <div className={styles.fieldLabelMobile}>{t('security-login-history-eventType')}</div>
      <div className={styles.fieldValueMobile}>{formatApplication(item?.eventType)}</div>
    </div>

    <div className={styles.fieldBlockMobile}>
      <div className={styles.fieldLabelMobile}>{t('security-login-history-deviceSystem')}</div>
      <div className={styles.fieldValueMobile}>{formatDeviceSystem(item)}</div>
    </div>

    <div className={styles.fieldBlockMobile}>
      <div className={styles.fieldLabelMobile}>{t('security-login-history-location')}</div>
      <div className={styles.fieldValueMobile}>{formatLocation(item)}</div>
    </div>

  </div>
)

function SecurityMobile({ t }) {
  const dispatch = useDispatch()
  const [startDateOpen, setStartDateOpen] = useState(false)
  const [endDateOpen, setEndDateOpen] = useState(false)
  const loginHistoryObj = useSelector(selectLoginHistoryObject)
  const status = useSelector(getLoginHistoryStatus)
  const error = useSelector(getLoginHistoryError)

  const { page, rowsPerPage } = useSelector(getLoginHistoryPagination)
  const { startDate, endDate } = useSelector(getLoginHistoryFilter)

  const rows = loginHistoryObj?.history || []
  const total = loginHistoryObj?.total || 0

  const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1)

  useEffect(() => {
    if (page > maxPage) {
      setPage(maxPage)
    }

    if (total <= rowsPerPage && page !== 0) {
      setPage(0)
    }
  }, [total, rowsPerPage])

  const handleChangePage = (_event, newPage) => {
    dispatch(setPage(newPage))
  }

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(+event.target.value))
    dispatch(setPage(0))
  }

  const handleStartDateChange = (value) => {
    dispatch(setStartDate(value))
    dispatch(setPage(0))

    if (value && endDate && dayjs(value).isAfter(dayjs(endDate), 'day')) {
      dispatch(setEndDate(null))
    }
  }

  const handleEndDateChange = (value) => {
    if (!value) {
      dispatch(setEndDate(value))
      dispatch(setPage(0))
      return
    }

    if (startDate && dayjs(value).isBefore(dayjs(startDate), 'day')) {
      dispatch(setEndDate(null))
      dispatch(setPage(0))
      return
    }

    dispatch(setEndDate(value))
    dispatch(setPage(0))
  }

  const logsOnPage = rows.length

  return (

    <MarginWrapper>
      <div className={styles['mobile-my-account-container']}>
        <div className={styles['mobile-back-home-section']}>
          <Link
            to="/home"
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
            color="black"
          >
            <ArrowBackIosIcon
              className={styles['home-icon']}
              fontSize="inherit"
            />
            <div className={styles['home-text']}>{t('home-tab')}</div>
          </Link>
        </div>
      </div>
      <header style={{
        fontSize: '1.5rem', fontWeight: 'bold', padding: '9px 0px 0px 24px', display: 'flex'
      }}
      >{t('security-page-header')}
      </header>
      <h4 className={styles.subtitleMobile}>
        <Trans i18nKey="security-page-subtitle-mobile" />
      </h4>
      <div className={styles.containerMobile}>

        {status === 'loading' && (
        <div className={styles.stateMsg}>
          <div className={styles.loadingBox}>
            <CircularProgress size={36} thickness={4} />
          </div>
        </div>
        )}

        {status === 'failed' && (
        <div className={styles.stateMsg}>{error || t('generic-error')}</div>
        )}

        {status === 'succeeded' && (
        <div
          className={styles.filtersBar}
          role="region"
          aria-label="Filters"
        >
          <div className={styles.filtersRowMobile}>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabelMobile}>
                {t('select-start-date')}
              </span>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={startDate}
                  desktopModeMediaQuery="@media (min-width:0px)"
                  onChange={handleStartDateChange}
                  open={startDateOpen}
                  onOpen={() => setStartDateOpen(true)}
                  onClose={() => setStartDateOpen(false)}
                  disableFuture
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      placeholder: 'MM / DD / YYYY',
                      className: styles.filterInput,
                      variant: 'outlined',
                      onClick: () => setStartDateOpen(true),
                      inputProps: {
                        'aria-label': 'Start date',
                        'data-testid': 'start-date-input',
                        readOnly: true,
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className={styles.filterGroup}>
              <span className={styles.filterLabelMobile}>
                {t('select-end-date')}
              </span>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={endDate}
                  desktopModeMediaQuery="@media (min-width:0px)"
                  onChange={handleEndDateChange}
                  open={endDateOpen}
                  onOpen={() => setEndDateOpen(true)}
                  onClose={() => setEndDateOpen(false)}
                  disableFuture
                  maxDate={dayjs()}
                  minDate={startDate ? dayjs(startDate) : undefined}
                  slotProps={{
                    textField: {
                      placeholder: 'MM / DD / YYYY',
                      className: styles.filterInput,
                      variant: 'outlined',
                      onClick: () => setEndDateOpen(true),
                      inputProps: {
                        'aria-label': 'End date',
                        'data-testid': 'end-date-input',
                        readOnly: true,
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
        </div>
        )}

      </div>

      <div className={styles.mobileLeftAlignStyle}>

        {status === 'succeeded' && (
        <>

          <hr className={styles.topLine} />

          {Array.isArray(rows) && rows.length === 0 ? (
            <div className={styles.stateMsgNoFoundMobile}>
              {t('security-page-message')}
            </div>
          ) : (
            <div className={styles.cardListMobile}>

              { rows.map((entity, index) => renderLoginHistoryMobile(entity, index, t))}

              <div className={styles.paginationMobile}>
                <hr className={styles.bottomLineMobile} />
                <TablePagination
                  component="div"
                  className={styles.tablePagination}
                  data-testid="logs-per-page-of"
                  count={total}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[25, 50]}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t('logs-per-page')}
                  labelDisplayedRows={({ from, count }) =>
                    `${t('logs-displaying')} ${from} -  ${from + logsOnPage - 1} ${t(
                      'logs-per-page-of',
                    )} ${count}`}
                  sx={{
                    color: '#564C47',
                    '@media (max-width:600px)': {
                      '& .MuiTablePagination-toolbar': {
                        flexWrap: 'wrap',
                      },
                      '& .MuiTablePagination-selectLabel': {
                        marginLeft: '25px'
                      },
                      '& .MuiTablePagination-input': {
                        marginLeft: '24px',
                        marginRight: '77px'
                      },
                      '& .MuiTablePagination-displayedRows': {
                        marginLeft: 'auto',
                        marginRight: '30px'
                      },
                      '& .MuiTablePagination-actions': {
                        marginRight: '20px',
                      },
                    }

                  }}
                />
              </div>
            </div>

          )}
          <div className={styles.cardListMobile}>
            <div className={styles['jnj-footer']}>
              <JNJFooter text={t('FooterText')} footerSize="medium" />
            </div>
          </div>
        </>
        )}

      </div>
    </MarginWrapper>
  )
}

SecurityMobile.propTypes = {
  t: PropTypes.func.isRequired
}

export default SecurityMobile
